"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Calendar, Tag, ListChecks, Rocket, Edit3 } from "lucide-react";

const STEPS = [
  { icon: Edit3, label: { en: "Name", es: "Nombre" }, time: "01:12" },
  { icon: Calendar, label: { en: "Date", es: "Fecha" }, time: "01:48" },
  { icon: Tag, label: { en: "Tiers", es: "Tiers" }, time: "03:54" },
  { icon: ListChecks, label: { en: "Checklist", es: "Checklist" }, time: "06:30" },
  { icon: Rocket, label: { en: "Live", es: "En vivo" }, time: "08:47" },
];

export default function EventTicketingWizardScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      {/* Atmosphere */}
      <div
        className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Idea → live in 10 minutes", "Idea → en vivo en 10 minutos")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("10 minutes ", "10 minutos ")}
            <span className="text-white/30">{t("to live.", "para estar en vivo.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "No setup calls. No \"contact sales\". Once your payments are set up, you decide to sell tickets at 6:00 PM and they're on sale by 6:10. Wizard runs you through it.",
              "Sin llamadas de setup. Sin \"contact sales\". Con tus pagos ya configurados, decides vender tickets a las 6:00 PM y a las 6:10 ya están en venta. El wizard te guía."
            )}
          </p>
        </motion.div>

        {/* Wizard mockup card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-8 md:p-10 max-w-[920px] mx-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-1">
                {t("Create event", "Crear evento")}
              </div>
              <div className="text-[20px] font-black text-white tracking-tight">
                NEON RUSH · Friday Dec 13
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                {t("Elapsed", "Tiempo")}
              </div>
              <div className="text-[24px] font-black tabular-nums" style={{ color: "#F472B6" }}>
                08:47
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="relative h-1.5 bg-white/10 rounded-full mb-10 overflow-hidden">
            <motion.div
              initial={{ width: "0%" }}
              whileInView={{ width: "88%" }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 2.5, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                background: "linear-gradient(90deg, #7C3AED 0%, #F472B6 100%)",
              }}
            />
          </div>

          {/* Steps */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isLast = i === STEPS.length - 1;
              return (
                <motion.div
                  key={step.label.en}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.45, delay: 0.6 + 0.15 * i }}
                  className="flex flex-col items-center text-center"
                >
                  <div
                    className="w-12 h-12 rounded-[14px] flex items-center justify-center mb-3 relative"
                    style={{
                      background: isLast
                        ? "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)"
                        : "rgba(124,58,237,0.15)",
                    }}
                  >
                    {isLast ? (
                      <Rocket className="w-5 h-5 text-white" strokeWidth={2.5} />
                    ) : (
                      <>
                        <Icon className="w-5 h-5" style={{ color: "#C4B5FD" }} />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.5 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true, margin: "-80px" }}
                          transition={{ duration: 0.3, delay: 0.9 + 0.15 * i }}
                          className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: "#2ECC71" }}
                        >
                          <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                        </motion.div>
                      </>
                    )}
                  </div>
                  <div className="text-[12px] font-black text-white mb-1">
                    {t(step.label.en, step.label.es)}
                  </div>
                  <div
                    className="text-[10px] font-bold tabular-nums"
                    style={{
                      color: isLast ? "#F472B6" : "rgba(255,255,255,0.4)",
                    }}
                  >
                    {step.time}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Footer message */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 1.8 }}
            className="mt-10 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/10 px-4 py-2.5 rounded-full">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#F472B6" }} />
              <span className="text-[12px] font-bold text-white/85">
                {t("Tickets on sale at useredy.com/neonrush", "Tickets en venta en useredy.com/neonrush")}
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
